// src/pages/Power.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import API from "../../../api";

export default function Power() {
  const { projectId } = useParams();

  const [rows, setRows] = useState([]);
  const [avgRuntime, setAvgRuntime] = useState(0);
  const [avgWorkingDays, setAvgWorkingDays] = useState(0);
  const [projectYears, setProjectYears] = useState([]);
  const [capUtil, setCapUtil] = useState([]);
  const [productionByYear, setProductionByYear] = useState([]);
  const [totalAnnualProduction, setTotalAnnualProduction] = useState(0);
  const [termLoanInput, setTermLoanInput] = useState({ startMonth: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const prod = useMemo(() => {
    const s = termLoanInput.startMonth || "";
    const [m, y] = s.split("-").map((t) => parseInt(t, 10));
    return m && y ? { month: m, year: y } : null;
  }, [termLoanInput.startMonth]);

  const getMonthsActive = (fyLabel) => {
    if (!prod) return 12;
    const fyStart = parseInt(fyLabel.split("-")[0], 10);
    if (isNaN(fyStart)) return 12;
    const fyEnd = fyStart + 1;
    const raw = (fyEnd - prod.year) * 12 + (3 - prod.month + 1);
    return Math.max(0, Math.min(raw, 12));
  };

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    API.get(`/user/project-inputs/${projectId}`)
      .then(({ data }) => {
        setRows(data.powerSectionData || []);
        setTermLoanInput(data.termLoanInput || { startMonth: "" });

        const rmData = data.rmConsumptionInputs || [];
        if (rmData.length) {
          const totalHours = rmData.reduce(
            (sum, r) => sum + (parseFloat(r.workingHoursPerDay) || 0),
            0
          );
          const totalDays = rmData.reduce(
            (sum, r) => sum + (parseFloat(r.workingDays) || 0),
            0
          );
          setAvgRuntime(totalHours / rmData.length);
          setAvgWorkingDays(totalDays / rmData.length);
        }

        const years = data.projectYears || [];
        const util = data.capacityUtilizationPerYear || [];
        setProjectYears(years);
        setCapUtil(util);

        const installedArr = (data.rmConsumptionInputs || []).map((r) => {
          const d = parseFloat(r.workingDays) || 0;
          const h = parseFloat(r.workingHoursPerDay) || 0;
          const o = parseFloat(r.outputPerHour) || 0;
          return (d * h * o) / 1000;
        });
        const totalInstalled = installedArr.reduce((s, v) => s + v, 0);
        setTotalAnnualProduction(totalInstalled);

        const prodByYear = years.map((fy, i) => {
          const u = parseFloat(util[i]) || 0;
          const months = getMonthsActive(fy);
          return installedArr
            .map((inst) => (inst * u * months) / (100 * 12))
            .reduce((s, v) => s + v, 0);
        });
        setProductionByYear(prodByYear);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load Power data.");
      })
      .finally(() => setLoading(false));
  }, [projectId, prod]);

  const format = (n) =>
    typeof n === "number"
      ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 })
      : "";

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center w-full">
        <svg
          className="animate-spin h-10 w-10 text-gray-500"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
          />
        </svg>
      </div>
    );
  }
  if (error) {
    return <div className="text-red-600 text-center py-8 w-full">{error}</div>;
  }

  const totalLoad =
    parseFloat(
      rows.find((r) => r.particular === "Total Connected Power Load (KW)")
        ?.value
    ) || 0;
  const powerFactor =
    parseFloat(rows.find((r) => r.particular === "Power Factor")?.value) || 0;
  const avgRate =
    parseFloat(
      rows.find((r) => r.particular === "Average Rate per Unit")?.value
    ) || 0;
  const fixedCostPerMonth =
    parseFloat(
      rows.find((r) => r.particular === "Fixed Cost (Per Month)")?.value
    ) || 0;

  const totalUnitConsumptionPerDay = totalLoad * powerFactor * avgRuntime;
  const totalCostPerDay = totalUnitConsumptionPerDay * avgRate;
  const totalFixedCostPerYear = fixedCostPerMonth * 12;
  const totalVariableCostPerAnnum = totalCostPerDay * avgWorkingDays;
  const totalElectricityCostPerAnnum =
    totalVariableCostPerAnnum + totalFixedCostPerYear;
  const costPerMT =
    totalAnnualProduction > 0
      ? totalElectricityCostPerAnnum / totalAnnualProduction
      : 0;

  return (
    <div className="bg-white space-y-12">
      {/* Power Inputs Table */}
      <section>
        <div className="overflow-x-auto shadow-lg rounded-lg">
          <table className="min-w-full table-auto border-collapse">
            <thead className="bg-gray-100">
              <tr className="h-8">
                <th className="px-2 py-1 border border-gray-200 text-left font-medium text-sm">
                  Particulars
                </th>
                <th className="px-2 py-1 border border-gray-200 text-left font-medium text-sm">
                  Value
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="even:bg-gray-50">
                  <td className="px-2 py-1 border border-gray-200 text-sm">
                    {r.particular}
                  </td>
                  <td className="px-2 py-1 border border-gray-200 text-sm">
                    {r.value}
                  </td>
                </tr>
              ))}
              {[
                ["Average Running time per day", avgRuntime],
                ["Average Working days per Annum", avgWorkingDays],
                ["Total Unit Consumption per day", totalUnitConsumptionPerDay],
                ["Total Cost per Day (Rs)", totalCostPerDay],
                ["Variable Cost per Annum", totalVariableCostPerAnnum],
                [
                  "Total Variable Cost for Electricity (A)",
                  totalElectricityCostPerAnnum,
                ],
                ["Fixed Cost per Year", totalFixedCostPerYear],
                ["Total Annual Production", totalAnnualProduction],
                ["Cost Per MT of FG", costPerMT],
              ].map(([label, val], idx) => (
                <tr key={idx} className="bg-gray-200 font-semibold">
                  <td className="px-2 py-1 border border-gray-200 text-sm">
                    {label}
                  </td>
                  <td className="px-2 py-1 border border-gray-200 text-sm">
                    {format(val)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Year-wise Power Cost Summary */}
      <section>
        <h2 className="text-xl font-semibold mb-4 text-gray-800">
          Year-wise Power Cost Summary
        </h2>
        <div className="overflow-x-auto shadow-lg rounded-lg">
          <table className="min-w-full table-auto border-collapse">
            <thead className="bg-gray-100">
              <tr className="h-8">
                <th className="px-2 py-1 border border-gray-200 text-left font-medium text-sm">
                  Year
                </th>
                <th className="px-2 py-1 border border-gray-200 text-right font-medium text-sm">
                  Production (MT)
                </th>
                <th className="px-2 py-1 border border-gray-200 text-right font-medium text-sm">
                  Cost/MT (Rs)
                </th>
                <th className="px-2 py-1 border border-gray-200 text-right font-medium text-sm">
                  Total Cost (Lakhs)
                </th>
              </tr>
            </thead>
            <tbody>
              {projectYears.map((fy, i) => {
                const prodMT = productionByYear[i] || 0;
                const totalCostLakhs = (prodMT * costPerMT) / 100000;
                return (
                  <tr key={i} className="even:bg-gray-50">
                    <td className="px-2 py-1 border border-gray-200 text-sm">
                      {fy}
                    </td>
                    <td className="px-2 py-1 border border-gray-200 text-sm text-right">
                      {format(prodMT)}
                    </td>
                    <td className="px-2 py-1 border border-gray-200 text-sm text-right">
                      {format(costPerMT)}
                    </td>
                    <td className="px-2 py-1 border border-gray-200 text-sm text-right">
                      {format(totalCostLakhs)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
